import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Database, RotateCcw, Search } from 'lucide-react'
import { dataCubeApi } from '@/api/data-cube'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { DataCubeListParams } from '@/types/data-cube'

export function UserWeightPage() {
	const [filters, setFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const [searchFilters, setSearchFilters] = useState<DataCubeListParams>({ scope: 'my' })
	const { data = [], isLoading, isFetching } = useQuery({
		queryKey: ['data-cube', 'user-weight', searchFilters],
		queryFn: () => dataCubeApi.userWeight(searchFilters),
	})

	const handleSearch = () => setSearchFilters(cleanParams(filters))
	const handleReset = () => {
		setFilters({ scope: 'my' })
		setSearchFilters({ scope: 'my' })
	}

	return (
		<div className="space-y-5">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">员工权重</h1>
				<p className="mt-1 text-sm text-muted-foreground">按破冰和新开指标计算员工客户分配档位</p>
			</div>

			<Card className="rounded-lg">
				<CardContent className="pt-6">
					<div className="flex flex-wrap items-center gap-3">
					<DepartmentScopeFilter value={{ scope: filters.scope || 'my', department_id: filters.department_id }} onChange={(scopeValue) => setFilters({ ...filters, department_id: undefined, ...scopeValue, /* 本页未启用「无归属」 */ scope: scopeValue.scope === 'unassigned' ? undefined : scopeValue.scope })} />
						<DateRangePicker value={{ start_date: filters.start_date, end_date: filters.end_date }} onChange={(value) => setFilters({ ...filters, ...value })} />
						<Button type="button" onClick={handleSearch} disabled={isFetching}>
							<Search className="mr-1.5 h-4 w-4" />
							查询
						</Button>
						<Button type="button" variant="outline" onClick={handleReset}>
							<RotateCcw className="mr-1.5 h-4 w-4" />
							重置
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card className="overflow-hidden rounded-lg">
				<CardContent className="p-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[760px]">
							<TableHeader>
								<TableRow>
									<TableHead className="w-20">档</TableHead>
									<TableHead className="w-24">客户</TableHead>
									<TableHead className="w-44">员工</TableHead>
									<TableHead>破冰</TableHead>
									<TableHead>新开</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									<TableRow>
										<TableCell colSpan={5} className="h-28 text-center">加载中...</TableCell>
									</TableRow>
								) : data.length === 0 ? (
									<TableRow>
										<TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
											<Database className="mx-auto mb-2 h-8 w-8" />
											暂无员工权重
										</TableCell>
									</TableRow>
								) : data.map((row) => (
									<TableRow key={row.user_id}>
										<TableCell className="font-medium">{row.tier}</TableCell>
										<TableCell>{row.customer_count}</TableCell>
										<TableCell>{row.user?.nickname || row.user?.username || row.user_id}</TableCell>
										<TableCell>{row.breaking_count}</TableCell>
										<TableCell>{row.newly_count}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

function cleanParams(params: DataCubeListParams): DataCubeListParams {
	return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '')) as DataCubeListParams
}
